/**
 * What a subscription record (and Pro access given by hand) lets a person do. Pure: the
 * app, the plan page and the tests read the plan through these.
 */
import { grantIsActive, type ProGrant } from './proGrant';
import { SUBSCRIPTION_LIMITS, type SubscriptionTier, type UserSubscription } from '../types';

/** The plan in force: a grant first, then a running period, then unused credits */
export const getEffectiveTier = (subscription?: UserSubscription, grant?: ProGrant | null): SubscriptionTier => {
  // Access given by hand (utils/proGrant.ts) counts before anything paid for
  if (grantIsActive(grant)) return grant.tier;
  if (!subscription) return 'free';

  // A running period: the person is Pro until it ends
  if (subscription.currentCreditExpiresAt && Date.now() < subscription.currentCreditExpiresAt && subscription.status === 'active') {
    return 'pro';
  }

  // Unused credits: a period can be started
  if (subscription.credits && subscription.credits > 0 && subscription.status === 'active') {
    return subscription.tier || 'pro';
  }

  // Legacy LemonSqueezy check
  if (subscription.lemonSqueezySubscriptionId && subscription.status === 'active') {
    return subscription.tier || 'pro';
  }

  return 'free';
};

/** Whether the subscription is good now */
export const isSubscriptionValid = (subscription?: UserSubscription): boolean => {
  if (!subscription) return false;

  if (subscription.credits && subscription.credits > 0) {
    // The running period has ended: a new one has to start
    if (subscription.currentCreditExpiresAt && Date.now() > subscription.currentCreditExpiresAt) {
      return false;
    }
    return subscription.status === 'active';
  }

  // Legacy check
  return subscription.status === 'active' || subscription.status === 'on_trial';
};

/** What the plan allows, for the app */
export const useFeatureAccess = (subscription?: UserSubscription, grant?: ProGrant | null) => {
  const tier = getEffectiveTier(subscription, grant);
  const limits = SUBSCRIPTION_LIMITS[tier];
  const credits = subscription?.credits || 0;

  return {
    tier,
    limits,
    credits,
    canCreateWorkspace: (currentCount: number) =>
      limits.maxWorkspaces === -1 || currentCount < limits.maxWorkspaces,
    canAddNode: (currentCount: number) =>
      limits.maxNodesPerWorkspace === -1 || currentCount < limits.maxNodesPerWorkspace,
    canGenerateAI: (dailyCount: number) =>
      limits.aiGenerationsPerDay === -1 || dailyCount < limits.aiGenerationsPerDay,
    canGenerateVideo: limits.videoGenerationEnabled,
    hasWatermark: limits.exportWatermark,
    isPro: tier === 'pro' || tier === 'enterprise',
    isEnterprise: tier === 'enterprise',
    hasCredits: credits > 0,
  };
};
